import {
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useBoolean,
} from "@chakra-ui/react";
import { IconBase } from "components/base";
import OfflineModeTutorialModal from "components/modal/OfflineModeTutorialModal";
import {
  CacheProjectStatus,
  CacheProjectState,
} from "libs/cache-project/types";
import { memo, useEffect, useMemo } from "react";
import { DATE_TIME_SLASH_WITHOUT_SECONDS_FORMAT, formatDate } from "utils/date";
import DownloadProjectIcon from "./DownloadProjectIcon";

let timeoutShowWarningToSyncData: NodeJS.Timeout;

interface Props {
  isOnline: boolean;
  cachingBimFileId: string;
  bimfileId: string;
  isPageForgeViewer?: boolean;
  cacheProjectState: CacheProjectState | undefined;

  stopCacheProject: () => void;
  syncNewProjectData: () => Promise<void>;
}

const SyncDataForOfflineIcon = (props: Props) => {
  const {
    cachingBimFileId,
    isOnline,
    bimfileId,
    cacheProjectState,
    stopCacheProject,
    syncNewProjectData,
  } = props;

  const [isOpenOfflineTutorial, setIsOpenOfflineTutorial] = useBoolean(false);
  const [isShowWarningSyncData, setIsShowWarningSyncData] = useBoolean();

  const isCaching = useMemo(
    () =>
      cacheProjectState?.status === CacheProjectStatus.PROCESSING ||
      cacheProjectState?.status === CacheProjectStatus.STOPPING,
    [cacheProjectState?.status]
  );
  const syncDataTime = useMemo(
    () =>
      cacheProjectState?.lastUpdated
        ? new Date(cacheProjectState?.lastUpdated)
        : null,
    [cacheProjectState?.lastUpdated]
  );

  useEffect(() => {
    if (!syncDataTime) {
      setIsShowWarningSyncData.off();

      return;
    }

    clearTimeout(timeoutShowWarningToSyncData);
    const localTime = new Date(syncDataTime).getTime();
    const now = new Date().getTime();
    const timeout = localTime + 60 * 60 * 24 * 1_000 - now;
    if (timeout < 0) {
      setIsShowWarningSyncData.on();
    } else {
      timeoutShowWarningToSyncData = setTimeout(() => {
        setIsShowWarningSyncData.on();
      }, Math.floor(timeout));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncDataTime]);

  const isDisabled = useMemo(() => {
    return (
      (!!cachingBimFileId && cachingBimFileId !== bimfileId) ||
      !isOnline
    );
  }, [bimfileId, cachingBimFileId, isOnline]);

  const handleSyncData = async () => {
    await syncNewProjectData();
  };

  const handleClickMenuDownloadProject = (onCloseMenu: () => void) => {
    onCloseMenu();

    if (isCaching) {
      stopCacheProject();
    } else {
      handleSyncData();
    }
  };

  return (
    <>
      <Menu autoSelect={false} closeOnSelect={false}>
        {({ isOpen, onClose }) => (
          <>
            <MenuButton
              isActive={isOpen}
              as={Button}
              aria-label="Options"
              isDisabled={isDisabled}
              leftIcon={
                <DownloadProjectIcon
                  cacheProjectState={cacheProjectState}
                  isDisabled={isDisabled}
                />
              }
              border="none"
              background="transparent !important"
              boxShadow="unset !important"
              fontSize="1.2rem"
              color="font.gray"
              height="auto"
              variant="text"
              whiteSpace={{ base: "normal", xl: "nowrap" }}
              rightIcon={
                <IconBase
                  icon="/img/arrow-right3.svg"
                  transition="0.4s"
                  color="#737373"
                  transform={isOpen ? "rotate(270deg)" : "rotate(90deg)"}
                />
              }
              lineHeight="1.8rem"
              textAlign="left"
            >
              <Text>オフラインモード使用前準備 </Text>
              <Text ml={syncDataTime ? 0 : "-0.7rem"}>
                {syncDataTime
                  ? `最終DL ${formatDate(
                      syncDataTime,
                      DATE_TIME_SLASH_WITHOUT_SECONDS_FORMAT
                    )}`
                  : "（事前にダウンロードが必要です）"}
              </Text>

              {syncDataTime && (
                <Text color={isShowWarningSyncData ? "font.danger" : "#D97706"}>
                  {isShowWarningSyncData
                    ? "最新データ再ダウンロード推奨"
                    : "※出来るだけ最新のデータを活用推奨"}
                </Text>
              )}
            </MenuButton>

            <MenuList
              maxWidth="25rem"
              minWidth="25rem"
              padding="0px"
              margin="0px"
              background="#fff"
              borderRadius="6px"
              whiteSpace={"normal"}
              boxShadow="0px 1px 3px 0px #0000004D, 0px 4px 8px 3px #00000026"
            >
              <MenuItem
                p="0.4rem 1.2rem"
                fontSize="1.2rem"
                color="font.gray"
                _hover={{ bgColor: "#F0F9FF !important" }}
                onClick={() => handleClickMenuDownloadProject(onClose)}
                icon={
                  <DownloadProjectIcon
                    cacheProjectState={cacheProjectState}
                    isDisabled={isDisabled}
                  />
                }
                lineHeight="1.8rem"
                textAlign="left"
              >
                <Text>最新データをダウンロード</Text>
                <Text ml="-7px">（所要時間： 数分〜数十分）</Text>
              </MenuItem>

              <MenuItem
                p="0.4rem 1.2rem"
                fontSize="1.2rem"
                color="font.gray"
                _hover={{ bgColor: "#F0F9FF !important" }}
                onClick={setIsOpenOfflineTutorial.on}
                icon={
                  <IconBase
                    icon="/img/icon-support.svg"
                    width="2.8rem"
                    height="2.8rem"
                    color="#171717"
                  />
                }
                lineHeight="1.8rem"
                textAlign="left"
              >
                <Text>オフラインモードの</Text>
                <Text>使い方・注意点</Text>
              </MenuItem>
            </MenuList>
          </>
        )}
      </Menu>

      {isOpenOfflineTutorial && (
        <OfflineModeTutorialModal
          isOpen={isOpenOfflineTutorial}
          onClose={setIsOpenOfflineTutorial.off}
        />
      )}
    </>
  );
};

export default memo(SyncDataForOfflineIcon);